// Report a message (App Store 1.2 UGC requirement). One reason, an
// optional note, then a thank-you with "Also block {name}" in the same
// dialog. Reports land in Synapse's event reports for the operator.

import { useId, useState } from "react";

import { BlockDialog } from "~/components/BlockDialog";
import { Dialog } from "~/components/Dialog";
import { useMatrix } from "~/context/MatrixContext";
import { REPORT_NOTE_MAX, REPORT_REASONS, reportEvent, type ReportReason } from "~/lib/matrix";

export interface ReportDialogProps {
  roomId: string;
  eventId: string;
  senderId: string;
  senderName: string;
  onClose: () => void;
}

export function ReportDialog({
  roomId,
  eventId,
  senderId,
  senderName,
  onClose,
}: ReportDialogProps) {
  const { client } = useMatrix();
  const titleId = useId();
  const noteId = useId();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState("");
  const [phase, setPhase] = useState<"form" | "sending" | "sent" | "block">("form");
  const [error, setError] = useState<string | null>(null);

  if (phase === "block") {
    return <BlockDialog userId={senderId} name={senderName} onClose={onClose} />;
  }

  const submit = async () => {
    if (!client || !reason) return;
    setPhase("sending");
    setError(null);
    try {
      await reportEvent(client, roomId, eventId, reason, note);
      setPhase("sent");
    } catch (err) {
      console.warn("reportEvent failed", err);
      setError("Couldn't send the report. Try again.");
      setPhase("form");
    }
  };

  return (
    <Dialog labelledBy={titleId} onClose={onClose} width="420px">
      <div className="flex flex-col gap-5 p-5">
        {phase === "sent" ? (
          <>
            <div className="space-y-2">
              <h2 id={titleId} className="text-base font-light text-foreground/90">
                Thanks. We'll review this message.
              </h2>
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setPhase("block")}
                className="h-11 text-sm font-light text-destructive-text outline-none hover:underline focus-visible:shadow-neon-cyan"
              >
                Also block {senderName}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="h-11 rounded-lg bg-foreground px-5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-80 focus-visible:shadow-neon-cyan"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id={titleId} className="text-base font-light text-foreground/90">
              Report message
            </h2>
            <div role="radiogroup" aria-labelledby={titleId} className="-mx-2 flex flex-col">
              {REPORT_REASONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={reason === option}
                  onClick={() => setReason(option)}
                  className="flex h-11 items-center gap-3 rounded-md px-2 text-left text-base font-light text-foreground/90 outline-none transition-colors hover:bg-foreground/[0.03] focus-visible:shadow-neon-cyan"
                >
                  <span
                    aria-hidden
                    className={`flex size-4 shrink-0 items-center justify-center rounded-full border ${
                      reason === option ? "border-foreground" : "border-foreground/30"
                    }`}
                  >
                    {reason === option && <span className="size-2 rounded-full bg-foreground" />}
                  </span>
                  {option}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={noteId} className="text-xs font-light text-muted-foreground">
                Add a note (optional)
              </label>
              <textarea
                id={noteId}
                value={note}
                maxLength={REPORT_NOTE_MAX}
                rows={2}
                onChange={(event) => setNote(event.target.value)}
                className="resize-none rounded-md bg-foreground/[0.04] px-3 py-2 text-base font-light text-foreground/90 outline-none focus-visible:shadow-neon-cyan"
              />
            </div>
            {error && (
              <p role="alert" className="text-sm font-light text-destructive-text">
                {error}
              </p>
            )}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={onClose}
                className="h-11 text-sm font-light text-muted-foreground outline-none hover:text-foreground/80 focus-visible:shadow-neon-cyan"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!reason || phase === "sending"}
                onClick={() => void submit()}
                className="h-11 rounded-lg bg-foreground px-5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-80 focus-visible:shadow-neon-cyan disabled:opacity-40"
              >
                Report
              </button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
